import { lineTotalCents, parseMoneyToCents, tt, type MoneyInput } from '@agos/shared'

/**
 * Splitting a table's bill.
 *
 * Four people at a table, one of them leaving: they pay for what they ate and
 * the rest of the bill stays open. The original order is closed and re-issued
 * as two — a completed one for what was paid and a live one for what is left —
 * and a fiscal receipt is printed for the paid half only.
 *
 * Ported from legacy `pages/restaurant.tsx`. It is the most consequential
 * arithmetic in the module: a quantity that lands on the wrong side of the
 * split either charges somebody twice or lets a table walk out with food it
 * did not pay for. Pure, and tested.
 */

export interface PayableLine {
  /**
   * `order_items.id` - what a ticked line is addressed by, §9.408.
   *
   * On the wire since the beginning: `OrderDtos.ListLine` and `DetailLine` both open with `Integer
   * id`. This interface simply never declared it, so the split was keyed by array position instead
   * and a tick slid onto other food whenever the board's eight-second poll replaced the array.
   */
  id?: number
  productId?: number
  productName?: string
  customName?: string
  quantity?: number
  unitPrice?: MoneyInput
  /**
   * The rate this line was **charged** at, snapshotted when the order was created — §9.5's per-line
   * VAT, on `order_items.vat_rate` and answered by the order detail. Prefer it over the pair below.
   */
  vatRate?: MoneyInput
  vatRateDineIn?: MoneyInput
  vatRateDelivery?: MoneyInput
  /**
   * §10A.110, the owner's Q6 - what the line **is**, carried through a split.
   *
   * These were dropped on both halves: a gift came back as a sale in the margin report and in a
   * customer's favourites, a daily-menu meal lost §10A.88's kitchen box, and a burger box's slots
   * came back loose. The money was never affected - the server conserves prices and re-prices
   * nothing.
   */
  giftRuleId?: number
  giftRuleType?: string
  lunchMealType?: string
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
}

export interface SplitLine {
  productId: number
  productName: string
  quantity: number
  unitPrice: number
  /** §10A.110 - optional on the wire, and absent means what it always meant. */
  giftRuleId?: number
  giftRuleType?: string
  lunchMealType?: string
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
}

/**
 * The identity fields a split half inherits from the line it came from - §10A.110.
 *
 * Spelled once, because the paid half and the remaining half are built separately and a field added
 * to one and forgotten on the other would split a gift into a gift and a sale.
 */
function identityOf(line: PayableLine): Omit<SplitLine, 'productId' | 'productName' | 'quantity' | 'unitPrice'> {
  return {
    ...(line.giftRuleId === undefined ? {} : { giftRuleId: line.giftRuleId }),
    ...(line.giftRuleType === undefined ? {} : { giftRuleType: line.giftRuleType }),
    ...(line.lunchMealType === undefined ? {} : { lunchMealType: line.lunchMealType }),
    ...(line.boxGroupId === undefined ? {} : { boxGroupId: line.boxGroupId }),
    ...(line.boxGroupName === undefined ? {} : { boxGroupName: line.boxGroupName }),
    ...(line.boxSlotLabel === undefined ? {} : { boxSlotLabel: line.boxSlotLabel }),
  }
}

export interface SplitResult {
  paidItems: SplitLine[]
  remainingItems: SplitLine[]
  paidCents: number
  remainingCents: number
}

/**
 * What a line is called when nothing named it. It reaches a fiscal receipt, so
 * it is not translated — the register prints Slovak whatever the manager's UI
 * language is.
 */
// eslint-disable-next-line no-restricted-syntax -- printed on the receipt, not shown in the UI
const UNNAMED_LINE = 'Položka'

function nameOf(line: PayableLine): string {
  return line.productName ?? line.customName ?? UNNAMED_LINE
}

/**
 * How a ticked line is addressed - §9.408.
 *
 * **`order_items.id`, not the line's position.** This was keyed by position, for a reason the old
 * docblock stated and which was right as far as it went: *"two lines can carry the same product
 * (FB-053, the same reason the cart is addressed by index)"*. A product id cannot tell three beers
 * rung up separately apart, so it was rejected - correctly. What it missed is that the row has its
 * own identity and the server has always sent it: `OrderDtos.ListLine` and `DetailLine` both open
 * with `Integer id`, filled from `item.getId()`.
 *
 * **Position is not stable data here.** `RestaurantPage` polls the order list every eight seconds so
 * a table cancelled in the kitchen is noticed, and the split itself *"closes the original order and
 * re-issues it"* - so the array a tick was made against is routinely replaced while the dialog is
 * open. The restaurant's report of 2026-09-21: *"označila si niečo a potom keď platila, tak to
 * označilo úplne niečo iné"*.
 *
 * **Negative for a line without an id**, so a fallback key can never collide with a real row. Every
 * line of a persisted order has one; this exists so the type need not pretend otherwise.
 */
export function lineKey(line: PayableLine | undefined, index: number): number {
  return line?.id ?? -(index + 1)
}

/**
 * Everything on the table, ticked - the whole bill as a split.
 *
 * Spelled once because it was spelled three times in `PaymentDialog` and keyed by index in all
 * three. A fourth copy, or one of them left behind on a change like §9.408's, divides a bill by a
 * different rule than the screen showed.
 */
export function wholeBill(lines: PayableLine[]): Map<number, number> {
  return new Map(lines.map((line, index) => [lineKey(line, index), line.quantity ?? 0]))
}

/**
 * Divides the bill by how many of each line the leaving guest is paying for.
 *
 * Partial quantities are the point: three beers on the table, one of them being
 * settled. `selectedByLine` is keyed by {@link lineKey} - the row's own id - which is what lets two
 * lines carrying the same product be told apart **and** survive the eight-second refresh the board
 * runs underneath this dialog. §9.408 has the report that found the difference.
 *
 * A selection larger than the line is clamped, and a negative one is ignored —
 * neither should reach here, and both would otherwise create a remainder that
 * charges the table for food it already paid for.
 */
export function splitSelection(
  lines: PayableLine[],
  selectedByLine: Map<number, number>,
): SplitResult {
  const paidItems: SplitLine[] = []
  const remainingItems: SplitLine[] = []
  let paidCents = 0
  let remainingCents = 0

  lines.forEach((line, index) => {
    const total = line.quantity ?? 0
    const selected = Math.max(0, Math.min(total, selectedByLine.get(lineKey(line, index)) ?? 0))
    const remaining = total - selected
    const unitPrice = parseMoneyToCents(line.unitPrice)

    if (selected > 0) {
      paidItems.push({
        productId: line.productId ?? 0,
        productName: nameOf(line),
        quantity: selected,
        unitPrice: unitPrice / 100,
        ...identityOf(line),
      })
      paidCents += lineTotalCents(line.unitPrice, selected)
    }
    if (remaining > 0) {
      remainingItems.push({
        productId: line.productId ?? 0,
        productName: nameOf(line),
        quantity: remaining,
        unitPrice: unitPrice / 100,
        ...identityOf(line),
      })
      remainingCents += lineTotalCents(line.unitPrice, remaining)
    }
  })

  return { paidItems, remainingItems, paidCents, remainingCents }
}

/** Nothing selected means nothing to split; the caller must not send it. */
export function hasSomethingToPay(selectedByIndex: Map<number, number>): boolean {
  return [...selectedByIndex.values()].some((quantity) => quantity > 0)
}

/**
 * The VAT rate a line is charged at.
 *
 * Slovakia taxes food eaten in at a different rate from food taken away, and
 * every product carries both. Reading the wrong one puts the wrong tax on a
 * fiscal receipt, which is the kind of error that is discovered at an audit.
 *
 * **The line's own rate wins — FB-192.** `vatRateDineIn`/`vatRateDelivery` are the product's rate
 * *now*; `vatRate` is the rate this line was registered at, snapshotted at creation. They differ the
 * moment somebody edits a product's VAT category, and any order already placed and unpaid then
 * printed the new rate on a tax document. The backend's reprint path documents this hazard and
 * avoids it; this path had it, because the snapshot only reached the order detail on 2026-08-04.
 *
 * The pair is kept as the fallback rather than removed: a line written before the column existed
 * carries no snapshot, and a receipt with no rate at all is worse than one with a stale rate.
 *
 * **That fallback was recorded here as unreachable and is not - §9.390, FB-695.** It said `vat_rate`
 * is set on 32 403 of 32 403 lines, which was true when it was written: two writers of `order_items`
 * stored no rate at all, so an edited or split order now has lines that reach the branch below.
 *
 * **And the branch read the three names, which is not the rule - §10A.164's own defect.** The
 * server's is the channel's `vat_slot`, then its `on_premise`, and the names only for a channel with
 * no row; `bar` was missing from the pair here, and so was every channel a franchise creates - each
 * of them taxed as *taken away* on food handed over on the premises. The channel is optional so a
 * caller that has not loaded one behaves exactly as before, which is what `paymentNeedsWaiter` does.
 */
export function vatRateFor(
  line: PayableLine,
  orderType: string | undefined,
  channel?: { vatSlot?: string | null; onPremise?: boolean },
): MoneyInput {
  if (line.vatRate !== null && line.vatRate !== undefined) return line.vatRate
  return eatenInOn(orderType, channel) ? line.vatRateDineIn : line.vatRateDelivery
}

/**
 * Whether the food is taxed as eaten in - the server's `VatSlots.isEatenIn`, in the same order.
 *
 * `vat_slot` answers it outright where a franchise has set one, because *who owns the order* and
 * *how the food is taxed* are two questions and *Do ruky* is where they differ (§10A.113). Without
 * a slot the channel's `on_premise` answers, and only a channel that does not exist falls back to
 * the three names this used to read on its own.
 */
function eatenInOn(
  orderType: string | undefined,
  channel?: { vatSlot?: string | null; onPremise?: boolean },
): boolean {
  if (channel !== undefined) {
    if (channel.vatSlot === 'dine_in') return true
    if (channel.vatSlot === 'delivery') return false
    if (channel.onPremise !== undefined) return channel.onPremise
  }
  return orderType === 'restaurant' || orderType === 'bar' || orderType === 'dine_in'
}

export interface ReceiptLine {
  name: string
  quantity: number
  unitPrice: number
  vatRate?: number
}

/** The lines as the register wants them, with the rate that applies. */
export function receiptLines(
  lines: PayableLine[],
  orderType: string | undefined,
  channel?: { vatSlot?: string | null; onPremise?: boolean },
): ReceiptLine[] {
  return lines.map((line) => {
    const vatRate = vatRateFor(line, orderType, channel)
    return {
      name: nameOf(line),
      quantity: line.quantity ?? 0,
      unitPrice: parseMoneyToCents(line.unitPrice) / 100,
      ...(vatRate !== null && vatRate !== undefined
        ? { vatRate: parseMoneyToCents(vatRate) / 100 }
        : {}),
    }
  })
}

/**
 * Warnings the register raises that a waiter must not be interrupted by.
 *
 * States 40, 41, 50 and 56 mean the till has lost its connection to the tax
 * authority's server. The receipt is still valid and the till reconnects and
 * uploads by itself. Toasting these mid-service trains staff to dismiss
 * register warnings without reading them — which is exactly what must not
 * happen when the certificate or the CHDÚ is the one complaining.
 */
export function isTransientKasaWarning(warning: unknown): boolean {
  const text = typeof warning === 'string' ? warning : JSON.stringify(warning ?? '')
  return (
    text.includes('offline/pending') ||
    text.includes('EKASA_OFFLINE_STATE50') ||
    /stav (40|41|50|56)/.test(text)
  )
}

/**
 * What the operator is asked before a table is closed - §9.366, FB-654.
 *
 * **The owner's report from a live restaurant:** *"platba kartou, it was automatically closed, and no
 * one used karta. When used hotovosť, bloček also did not printed and it was closed."* The pay button
 * ran straight off the click, and `payAll` closes the table **before** the terminal or the printer is
 * asked - so the close happened either way and the failure arrived afterwards as a toast.
 *
 * **The consequence named is specific to the method, because that is the half that was missing.**
 * *Are you sure* on its own would not have told anybody that the card had not been charged yet.
 */
export interface CloseTableConfirmation {
  title: string
  description: string
  confirmLabel: string
}

/**
 * The question after the payment - §9.367, FB-655.
 *
 * **Legacy asks it there and AGOS asked it before.** `restaurant.tsx:3370` shows *"Prebehla platba
 * úspešne?"* once the document is out, with *"Áno, zatvoriť stôl"* and *"Nie, zopakovať platbu"* -
 * so paying and closing are two decisions, and the owner's *"pay -> waiting for food"* is simply
 * answering the second one later.
 */
export interface CloseTableQuestion {
  title: string
  description: string
  closeLabel: string
  keepOpenLabel: string
}

export function closeTableQuestion(
  tableLabel: string,
  amount: string,
  options: { printed?: boolean } = {},
): CloseTableQuestion {
  return {
    title: tt('Prebehla platba úspešne?'),
    description:
      options.printed === false
        ? // The document did not print, so the close is offered with that said out loud rather than
          // hidden - somebody may still be closing a table that was paid in cash.
          `${tableLabel} · ${amount} · ${tt('Doklad sa nevytlačil.')}`
        : `${tableLabel} · ${amount}`,
    closeLabel: tt('Áno, zatvoriť stôl'),
    // **Not *Zrušiť*.** The owner pressed cancel expecting the table to stay open, which is what it
    // does - so the button says that instead of naming an abandonment.
    keepOpenLabel: tt('Nie, nechať stôl otvorený'),
  }
}

export function closeTableConfirmation(
  paymentMethod: string,
  amount: string,
  options: { partial?: boolean } = {},
): CloseTableConfirmation {
  const what = options.partial === true ? tt('Zaplatiť vybrané') : tt('Uzavrieť stôl')
  const consequence =
    paymentMethod === 'card_terminal'
      ? tt('Suma sa načíta na platobnom terminále. Potvrďte až keď je terminál pripravený.')
      : paymentMethod === 'cash'
        ? tt('Vytlačí sa fiškálny doklad. Ak sa nevytlačí, dozviete sa to hneď po potvrdení.')
        : tt('Platba sa zaznamená a doklad sa vytlačí.')
  return {
    title: options.partial === true ? `${what}?` : tt('Naozaj uzavrieť stôl?'),
    // The amount first: it is the one thing somebody checks before pressing a green button again.
    description: `${amount} · ${consequence}`,
    confirmLabel: what,
  }
}

/**
 * Whether this order has already been paid at the till - §9.369, FB-657.
 *
 * **`stampOrder` records it**: a printed receipt sets `fiskal_printed_at` and
 * `fiskal_receipt_number` on the order, so *paid* is a fact the screen can read.
 *
 * **And `fiskalStornoAt` is what makes it unpaid again.** A voided document means the money went
 * back, so the close-only shortcut must not be offered - reading `fiskalPrintedAt` alone would
 * offer to close a table whose payment had been reversed.
 */
export function alreadyPaid(order: {
  fiskalPrintedAt?: string
  fiskalStornoAt?: string
}): boolean {
  const printed = (order.fiskalPrintedAt ?? '').trim() !== ''
  const stornoed = (order.fiskalStornoAt ?? '').trim() !== ''
  return printed && !stornoed
}

/**
 * What to say over a table that is paid and still open.
 *
 * The receipt number is the point: it is the fact that tells *paid* from *somebody thinks it was
 * paid*, and it is the number they would quote if it had to be stornoed.
 */
export function alreadyPaidNote(order: { fiskalReceiptNumber?: string }): string {
  const number = (order.fiskalReceiptNumber ?? '').trim()
  return number === ''
    ? tt('Doklad pre tento stôl už bol vytlačený. Stôl zostal otvorený.')
    : `${tt('Doklad')} č. ${number} ${tt('už bol vytlačený. Stôl zostal otvorený.')}`
}

